// Auto-trim: find the bounding box of a logo's content so the crop modal can zoom straight onto it.
//
// Background = the image's corner colour when all four (opaque) corners agree, otherwise white.
// Fully transparent pixels always count as background. Pixels within `tol` (per channel) of the
// background are ignored. Mirrors the standalone 3SKYE Logo Resizer page.

export const DEFAULT_TOLERANCE = 10;
const ALPHA_BG = 16; // alpha at or below this is "transparent"
const SCAN_MAX = 1400; // scan a downscaled copy so large images stay fast
const NEGLIGIBLE_AREA = 0.99;

// Pure: scans an RGBA buffer and returns the content box in buffer pixels, or null when blank.
export function findContentBounds(data, w, h, tol = DEFAULT_TOLERANCE) {
	if (!data || !w || !h) return null;
	const px = (x, y) => {
		const i = (y * w + x) * 4;
		return [data[i], data[i + 1], data[i + 2], data[i + 3]];
	};
	const corners = [px(0, 0), px(w - 1, 0), px(0, h - 1), px(w - 1, h - 1)].filter((p) => p[3] > ALPHA_BG);
	let bg = [255, 255, 255];
	if (corners.length) {
		const first = corners[0];
		const agree = corners.every(
			(p) => Math.abs(p[0] - first[0]) <= tol && Math.abs(p[1] - first[1]) <= tol && Math.abs(p[2] - first[2]) <= tol
		);
		if (agree) bg = first;
	}
	const isBg = (i) =>
		data[i + 3] <= ALPHA_BG ||
		(Math.abs(data[i] - bg[0]) <= tol && Math.abs(data[i + 1] - bg[1]) <= tol && Math.abs(data[i + 2] - bg[2]) <= tol);

	let minX = w, minY = h, maxX = -1, maxY = -1;
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			if (isBg((y * w + x) * 4)) continue;
			if (x < minX) minX = x;
			if (x > maxX) maxX = x;
			if (y < minY) minY = y;
			if (y > maxY) maxY = y;
		}
	}
	if (maxX < 0) return null;
	return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

// Draws the loaded image to a (downscaled) canvas and returns the content box in SOURCE pixels.
// Returns null when the image is blank, already tight, or the canvas is tainted (cross-origin).
export function computeTrim(img, srcW, srcH, tol = DEFAULT_TOLERANCE) {
	if (!img || !srcW || !srcH) return null;
	const s = Math.min(1, SCAN_MAX / Math.max(srcW, srcH));
	const cw = Math.max(1, Math.round(srcW * s));
	const ch = Math.max(1, Math.round(srcH * s));
	const c = document.createElement('canvas');
	c.width = cw;
	c.height = ch;
	const ctx = c.getContext('2d', { willReadFrequently: true });
	if (!ctx) return null;
	let data;
	try {
		ctx.clearRect(0, 0, cw, ch);
		ctx.drawImage(img, 0, 0, cw, ch);
		data = ctx.getImageData(0, 0, cw, ch).data;
	} catch (e) {
		return null; // tainted canvas: leave the full image in place
	}
	const box = findContentBounds(data, cw, ch, tol);
	if (!box) return null;
	const x0 = Math.max(0, Math.floor(box.x / s));
	const y0 = Math.max(0, Math.floor(box.y / s));
	const x1 = Math.min(srcW, Math.ceil((box.x + box.w) / s));
	const y1 = Math.min(srcH, Math.ceil((box.y + box.h) / s));
	const rect = { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
	return isNegligibleTrim(rect, srcW, srcH) ? null : rect;
}

// A trim that removes less than 1% of the image's area (e.g. a single anti-aliased edge row) is not
// worth re-encoding the file for; callers treat it as "nothing to trim".
export function isNegligibleTrim(rect, srcW, srcH) {
	if (!rect) return true;
	return rect.w * rect.h >= NEGLIGIBLE_AREA * srcW * srcH;
}
